import * as React from 'react';
import PropTypes from 'prop-types';
import Label from '../../styled components/Label';
import TextInput from '../TextInput/TextInput';
import SearchField from '../SearchField/SearchField';
import { Meta } from '../../styled components/Typography';
import TextArea from '../Textarea/TextArea';
import DateInput from '../DateInput/DateInput';
import RadioGroup from '../RadioGroup/RadioGroup';
import Checkbox from '../Checkbox/Checkbox';
import Select from '../Select/Select';
import MessageBar from '../MessageBar/MessageBar';
import TimeInput from '../TimeInput/TimeInput';

const FFLabel = ({ children, ...restProps }) => {
  return <Label {...restProps}>{children}</Label>;
};

const FFDescriptive = ({ children, ...restProps }) => (
  <Meta as="p" {...restProps}>
    {children}
  </Meta>
);

const FFTextInput = ({ children, ...restProps }) => (
  <>
    <div tw="mt-8" />
    {/* Use a spacer as margin. Putting the margin directly on the input control component can cause inner absolute positioned elements to be placed in the wrong position */}
    <TextInput {...restProps} />
  </>
);

const FFSearchField = ({ children, ...restProps }) => (
  <>
    <div tw="mt-8" />
    {/* Use a spacer as margin. Putting the margin directly on the input control component can cause inner absolute positioned elements to be placed in the wrong position */}
    <SearchField {...restProps} />
  </>
);

const FFTextArea = ({ children, ...restProps }) => (
  <>
    <div tw="mt-8" />
    {/* Use a spacer as margin. Putting the margin directly on the input control component can cause inner absolute positioned elements to be placed in the wrong position */}
    <TextArea {...restProps} />
  </>
);

const FFDateInput = ({ children, ...restProps }) => (
  <>
    <div tw="mt-8" />
    {/* Use a spacer as margin. Putting the margin directly on the input control component can cause inner absolute positioned elements to be placed in the wrong position */}
    <DateInput {...restProps} />
  </>
);

const FFTimeInput = ({ children, ...restProps }) => (
  <>
    <div tw="mt-8" />
    {/* Use a spacer as margin. Putting the margin directly on the input control component can cause inner absolute positioned elements to be placed in the wrong position */}
    <TimeInput {...restProps} />
  </>
);

const FFRadioGroup = ({ children, ...restProps }) => (
  <>
    <div tw="mt-8" />
    {/* Use a spacer as margin. Putting the margin directly on the input control component can cause inner absolute positioned elements to be placed in the wrong position */}
    <RadioGroup>{children}</RadioGroup>
  </>
);

const FFCheckboxGroup = ({ children, ...restProps }) => (
  <>
    <div tw="mt-8" />
    {/* Use a spacer as margin. Putting the margin directly on the input control component can cause inner absolute positioned elements to be placed in the wrong position */}
    <div tw="flex flex-col space-y-4">{children}</div>
  </>
);
const FFCheckbox = ({ children, ...restProps }) => <Checkbox {...restProps} />;

const FFSelect = ({ children, ...restProps }) => (
  <>
    <div tw="mt-8" />
    {/* Use a spacer as margin. Putting the margin directly on the input control component can cause inner absolute positioned elements to be placed in the wrong position */}
    <Select {...restProps}></Select>
  </>
);

const FFErrorMessage = ({ children, ...restProps }) => (
  <MessageBar variant="error" size="small" tw="inline-block mt-8">
    {children}
  </MessageBar>
);

/**
 * With the FormField component you can combine a Label with a Descriptive and an Input Control.
 *
 * If an Input Control is invalid, an Error Message will be printed.
 *
 * Following the design requirements
 *
 *  - The child components should be printed in the following order:
 *
 *    + Label
 *    + Descriptive
 *    + Error Message
 *    + Input Control
 *  - All the child components are vertical stacked to each other.
 *  - All child components, except for the Input Control, are optional.
 *
 * Child components:
 *
 *  - Label
 *  - Descriptive
 *  - TextInput
 *  - TextArea
 *  - DateInput
 *  - RadioGroup
 *  - ErrorMessage
 *
 */

const FormField = (props) => {
  const { children, ...restProps } = props;
  return (
    <div className="" descriptive="false" {...restProps}>
      {children}
    </div>
  );
};

/**
 *  If you use these subcomponents, do not add the  tw property directly to them, this will cause jest tests to fail.
 */

FormField.Label = FFLabel;
FormField.Descriptive = FFDescriptive;
FormField.TextInput = FFTextInput;
FormField.SearchField = FFSearchField;
FormField.TextArea = FFTextArea;
FormField.DateInput = FFDateInput;
FormField.TimeInput = FFTimeInput;
FormField.RadioGroup = FFRadioGroup;
FormField.CheckboxGroup = FFCheckboxGroup;
FormField.Checkbox = FFCheckbox;
FormField.Select = FFSelect;
FormField.ErrorMessage = FFErrorMessage;

FormField.propTypes = {
  children: PropTypes.node.isRequired,
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className: PropTypes.string,
  /** Describe the value requirements */
  descriptive: PropTypes.string
};

export default FormField;
